import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Alert,
  Chip,
  Snackbar,
  Menu,
  MenuItem
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PreviewIcon from '@mui/icons-material/Preview';
import RestoreIcon from '@mui/icons-material/Restore';
import { SiteSettings } from './SiteCustomizationManager';
import { SiteCustomizationPreview } from './SiteCustomizationPreview';
import { ApiService } from '../services/api';

interface VersionHistoryViewerProps {
  onRestore: (settings: SiteSettings) => void;
  onRestoreAndPublish?: (versionId: string) => Promise<void>;
}

interface VersionEntry {
  version: string;
  publishedAt: string;
  publishedBy: string;
  publishedByEmail: string;
  settings: SiteSettings;
  changesSummary?: string;
}

export const VersionHistoryViewer: React.FC<VersionHistoryViewerProps> = ({ onRestore, onRestoreAndPublish }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [versions, setVersions] = useState<VersionEntry[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<VersionEntry | null>(null);
  const [showPreview, setShowPreview] = useState(false);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showRestorePublishDialog, setShowRestorePublishDialog] = useState(false);
  const [isRestoring, setIsRestoring] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; version: VersionEntry } | null>(null);

  useEffect(() => {
    loadVersionHistory();
  }, []);

  const loadVersionHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await ApiService.getSiteSettingsHistory();
      
      if (response.success) {
        setVersions(response.data.versions || []);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load version history');
    } finally {
      setLoading(false);
    }
  };

  const handlePreview = (version: VersionEntry) => {
    setSelectedVersion(version);
    setShowPreview(true);
  };

  const handleRestoreClick = (version: VersionEntry) => {
    setSelectedVersion(version);
    setShowRestoreDialog(true);
  };

  const handleRestorePublishClick = (version: VersionEntry) => {
    setSelectedVersion(version);
    setShowRestorePublishDialog(true);
  };

  const handleRestoreConfirm = () => {
    if (selectedVersion) {
      onRestore(selectedVersion.settings);
      setShowRestoreDialog(false);
      setSelectedVersion(null);
    }
  };

  const handleRestorePublishConfirm = async () => {
    if (selectedVersion && onRestoreAndPublish) {
      try {
        setIsRestoring(true);
        await onRestoreAndPublish(selectedVersion.version);
        setShowRestorePublishDialog(false);
        setSelectedVersion(null);
        setSnackbarMessage('Version restored and published successfully!');
        // Reload version history
        await loadVersionHistory();
      } catch (err: any) {
        setSnackbarMessage(err.message || 'Failed to restore and publish version');
      } finally {
        setIsRestoring(false);
      }
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeSince = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) {
      return `${diffMins} minute${diffMins !== 1 ? 's' : ''} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours !== 1 ? 's' : ''} ago`;
    } else if (diffDays < 30) {
      return `${diffDays} day${diffDays !== 1 ? 's' : ''} ago`;
    } else {
      return formatDate(dateString);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error">{error}</Alert>
    );
  }

  if (versions.length === 0) {
    return (
      <Paper sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          No Version History
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Version history will appear here after you publish changes.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Version History
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        View and restore previous versions of your site customization.
      </Typography>

      <Paper>
        <List>
          {versions.map((version, index) => (
            <ListItem
              key={version.version}
              divider={index < versions.length - 1}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <ListItemText
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="subtitle1">
                      Version {version.version}
                    </Typography>
                    {index === 0 && (
                      <Chip label="Current" size="small" color="primary" />
                    )}
                  </Box>
                }
                secondary={
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Published {getTimeSince(version.publishedAt)} by {version.publishedByEmail}
                    </Typography>
                    {version.changesSummary && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {version.changesSummary}
                      </Typography>
                    )}
                  </Box>
                }
              />
              <ListItemSecondaryAction>
                <IconButton
                  edge="end"
                  aria-label="preview"
                  onClick={() => handlePreview(version)}
                  sx={{ mr: 1 }}
                >
                  <PreviewIcon />
                </IconButton>
                {index !== 0 && (
                  <IconButton
                    edge="end"
                    aria-label="more options"
                    onClick={(e) => setMenuAnchor({ element: e.currentTarget, version })}
                    color="primary"
                  >
                    <MoreVertIcon />
                  </IconButton>
                )}
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Preview Dialog */}
      <Dialog
        open={showPreview}
        onClose={() => setShowPreview(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Preview Version {selectedVersion?.version}
          <Typography variant="body2" color="text.secondary">
            Published {selectedVersion && formatDate(selectedVersion.publishedAt)}
          </Typography>
        </DialogTitle>
        <DialogContent>
          {selectedVersion && (
            <SiteCustomizationPreview settings={selectedVersion.settings} />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowPreview(false)}>Close</Button>
          {selectedVersion && versions[0]?.version !== selectedVersion.version && (
            <Button
              onClick={() => {
                setShowPreview(false);
                handleRestoreClick(selectedVersion);
              }}
              variant="contained"
              startIcon={<RestoreIcon />}
            >
              Restore This Version
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Restore Confirmation Dialog */}
      <Dialog
        open={showRestoreDialog}
        onClose={() => setShowRestoreDialog(false)}
      >
        <DialogTitle>Restore Version to Draft?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to restore version {selectedVersion?.version}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This will replace your current draft with the settings from this version. 
            You'll need to publish the changes to make them visible to guests.
          </Typography>
          {selectedVersion && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2">Version Details:</Typography>
              <Typography variant="body2">
                Published: {formatDate(selectedVersion.publishedAt)}
              </Typography>
              <Typography variant="body2">
                By: {selectedVersion.publishedByEmail}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRestoreDialog(false)}>Cancel</Button>
          <Button
            onClick={handleRestoreConfirm}
            variant="contained"
            color="primary"
            startIcon={<RestoreIcon />}
          >
            Restore to Draft
          </Button>
        </DialogActions>
      </Dialog>

      {/* Restore and Publish Confirmation Dialog */}
      <Dialog
        open={showRestorePublishDialog}
        onClose={() => !isRestoring && setShowRestorePublishDialog(false)}
      >
        <DialogTitle>Restore and Publish Version?</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will immediately publish the restored version to all guests!
          </Alert>
          <Typography>
            Are you sure you want to restore and publish version {selectedVersion?.version}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            This action will:
          </Typography>
          <Box component="ul" sx={{ mt: 1, pl: 2 }}>
            <li>Restore settings from this historical version</li>
            <li>Update both draft and published versions</li>
            <li>Create a new version entry for this restoration</li>
            <li>Make changes visible to all guests immediately</li>
            <li>Invalidate the CloudFront cache</li>
          </Box>
          {selectedVersion && (
            <Box sx={{ mt: 2, p: 2, backgroundColor: 'action.hover', borderRadius: 1 }}>
              <Typography variant="subtitle2">Version Details:</Typography>
              <Typography variant="body2">
                Published: {formatDate(selectedVersion.publishedAt)}
              </Typography>
              <Typography variant="body2">
                By: {selectedVersion.publishedByEmail}
              </Typography>
              {selectedVersion.changesSummary && (
                <Typography variant="body2">
                  Summary: {selectedVersion.changesSummary}
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRestorePublishDialog(false)} disabled={isRestoring}>
            Cancel
          </Button>
          <Button
            onClick={handleRestorePublishConfirm}
            variant="contained"
            color="primary"
            startIcon={<RestoreIcon />}
            disabled={isRestoring}
          >
            {isRestoring ? 'Restoring...' : 'Restore and Publish'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem
          onClick={() => {
            if (menuAnchor) {
              handleRestoreClick(menuAnchor.version);
              setMenuAnchor(null);
            }
          }}
        >
          <RestoreIcon sx={{ mr: 1 }} fontSize="small" />
          Restore to Draft
        </MenuItem>
        {onRestoreAndPublish && (
          <MenuItem
            onClick={() => {
              if (menuAnchor) {
                handleRestorePublishClick(menuAnchor.version);
                setMenuAnchor(null);
              }
            }}
          >
            <RestoreIcon sx={{ mr: 1 }} fontSize="small" />
            Restore and Publish
          </MenuItem>
        )}
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
      />
    </Box>
  );
};
